<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header>Attach numbers</template>
        <template v-slot:subheader>{{ props.campaignId }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <div data-test="number-picker">
                <vx-label size="md">Bandwidth numbers</vx-label>
                <vx-selectable
                    :modelValue="selectedRows.map((row) => row.phone_number)"
                    @update:modelValue="onNumbersChange"
                    size="lg"
                    placeholder="Search Bandwidth numbers"
                    multiple
                    noTags
                    excludeSelected
                    :clearable="false"
                    :closeOnSelect="false"
                    :reduce="(option: NumberOption) => option.value"
                    :selectable="(option: NumberOption) => option.selectable"
                    :loading="loadingNumbers"
                    :options="numberOptions"
                    @search="searchNumbers"
                    :error="pickerError"
                >
                    <template v-slot:option="{ option }">
                        <div class="flex flex-col" data-test="number-option" :data-phone="option.value">
                            <div>{{ option.label }}</div>
                            <div class="text-xs text-slate-500">{{ option.detail }}</div>
                        </div>
                    </template>
                </vx-selectable>
            </div>
            <div class="text-slate-500" data-test="remaining">
                {{ selectedRows.length }} selected · {{ remaining }} of {{ props.limit }} free
            </div>
            <!-- Always mounted, since a live region that arrives with its text is not read. -->
            <p class="sr-only" role="status" aria-live="polite" data-test="outside-brand-status">
                <template v-if="outsideBrandCount">
                    {{ outsideBrandCount }} of the selected numbers {{ 1 === outsideBrandCount ? 'is' : 'are' }}
                    not on a team with numbers under this brand yet
                </template>
            </p>
            <ul v-if="selectedRows.length" class="divide-y divide-slate-200 rounded-lg border border-slate-200">
                <li
                    v-for="row in selectedRows"
                    :key="row.phone_number"
                    class="flex items-center gap-2 px-4 py-2"
                    data-test="selected-number"
                    :data-phone="row.phone_number"
                >
                    <div class="flex grow flex-col">
                        <span>{{ formatPhoneNumber(row.phone_number) }}</span>
                        <span class="text-xs text-slate-500">{{ numberDetail(row) }}</span>
                    </div>
                    <vx-badge
                        v-if="outsideBrand(row)"
                        color="info"
                        size="xs"
                        title="Not on a team with numbers under this brand yet"
                        data-test="outside-brand"
                    >
                        Outside brand
                    </vx-badge>
                    <vx-button
                        @click="removeNumber(row)"
                        type="button"
                        size="sm"
                        color="muted"
                        :aria-label="`Remove ${formatPhoneNumber(row.phone_number)}`"
                        data-test="remove-number"
                    >
                        <font-awesome-icon widthAuto :icon="faXmark" />
                    </vx-button>
                </li>
            </ul>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-attach"
                    :disabled="confirmDisabled"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Attach
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, ref, watch } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcClient, adminTenDlcRegistrationClient } from '~/services';
import type { TcrPhoneNumber } from '~/services/adminTenDlcClient.types';
import type { BandwidthTnOptionOrder } from '~/services/adminTenDlcRegistrationClient.types';
import debounce from '~/utils/debounce';
import { joinFieldErrors } from '~/utils/validation';

type NumberOption = { value: string; label: string; detail: string; selectable: boolean };

const props = defineProps<{ campaignId: string; brandTeamIds: number[]; attachedNumbers: string[]; limit: number }>();
const emit = defineEmits<{ placed: [order: BandwidthTnOptionOrder] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();

const numberResults = ref<TcrPhoneNumber[]>([]);
const selectedRows = ref<TcrPhoneNumber[]>([]);
const loadingNumbers = ref(false);

const numberDetail = (number: TcrPhoneNumber): string =>
    `${null === number.team_id ? 'no team' : `team ${number.team_id} – ${number.team_name}`} · ${
        null === number.campaign_id ? 'unassigned' : `on ${number.campaign_id}`
    }`;

const toNumberOption = (number: TcrPhoneNumber): NumberOption => ({
    value: number.phone_number,
    label: formatPhoneNumber(number.phone_number),
    detail: numberDetail(number),
    selectable: !props.attachedNumbers.includes(number.phone_number),
});

// vue-select matches the selection to the options it is given, and a search only returns
// the first page, so the selected numbers are kept in the options on their own.
const numberOptions = computed(() =>
    [
        ...selectedRows.value.filter(
            (row) => !numberResults.value.some((result) => result.phone_number === row.phone_number)
        ),
        ...numberResults.value,
    ].map(toNumberOption)
);

const numbersCancelKey = Symbol('kiosk-tcr-campaign-attach-numbers');

const searchNumbers = debounce((query?: string) => {
    loadingNumbers.value = true;

    adminTenDlcClient
        .getTcrPhoneNumbers({ vendor: 'bandwidth', query }, { cancelKey: numbersCancelKey })
        .then((response) => {
            numberResults.value = response.data.data;
            loadingNumbers.value = false;
        })
        .catch((e) => {
            if (isCancel(e)) {
                return;
            }

            loadingNumbers.value = false;
            console.error(e);
            toasted.global.platform_error();
        });
}, 300);

const onNumbersChange = (values: string[] | null) => {
    selectedRows.value = (values ?? [])
        .map(
            (value) =>
                selectedRows.value.find((row) => row.phone_number === value) ??
                numberResults.value.find((row) => row.phone_number === value)
        )
        .filter((row): row is TcrPhoneNumber => undefined !== row);
};

const removeNumber = (row: TcrPhoneNumber) => {
    selectedRows.value = selectedRows.value.filter((selected) => selected.phone_number !== row.phone_number);
};

const remaining = computed(() => props.limit - props.attachedNumbers.length);
const outsideBrand = (row: TcrPhoneNumber): boolean =>
    null === row.team_id || !props.brandTeamIds.includes(row.team_id);
const outsideBrandCount = computed(() => selectedRows.value.filter(outsideBrand).length);

const pickerError = computed(() => joinFieldErrors($externalResults.value, 'phone_numbers'));

const confirmDisabled = computed(() => 0 === selectedRows.value.length || selectedRows.value.length > remaining.value);

const submitting = ref(false);

watch(
    visible,
    (open) => {
        if (!open) {
            return;
        }

        $externalResults.value = {};
        selectedRows.value = [];
        searchNumbers();
    },
    { immediate: true }
);

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = await adminTenDlcRegistrationClient.assignCampaignPhoneNumbers(props.campaignId, {
            phone_numbers: selectedRows.value.map((row) => row.phone_number),
        });

        emit('placed', data);
        visible.value = false;
    } catch (e) {
        await catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
